// Inventory: one screen. Left, the runner with the four weapon slots around them; right, every gun you own.
import { RARITIES, AMMO_TYPES } from '../../../common/src/data';
import { weaponStats, weaponName, Item } from '../../../common/src/rules';
import { CREW, maskIndex } from '../crew';
import { btn, rarityColor, chip, delta, esc, Ctx, Screen } from './kit';
import { weaponImg } from './weaponimg';
import { pager } from './vendors';
import { pelletSvg } from '../smoke-art';
import { kunaiImg } from '../kunai-art';

export const INV_CAP = 24;
const PER_PAGE = 8;

interface Row { label: string; v: number; text: string; fmt: (x: number) => string; goodUp: boolean }
/** The few numbers that matter, in a fixed order so two guns line up row by row. */
function rows(it: Item, d: Ctx['d']): Row[] {
  const ws = weaponStats(it, d), s = ws.spec, n = s.pellets ?? 1;
  const f0 = (x: number) => String(Math.round(x)), f1 = (x: number) => x.toFixed(1);
  const blade = s.kind === 'melee'; // blades have no magazine or reload
  return [
    { label: 'Damage', v: ws.damage * n, text: `${f1(ws.damage)}${n > 1 ? `×${n}` : ''}`, fmt: f1, goodUp: true },
    ...(blade ? [] : [{ label: 'Magazine', v: ws.mag, text: f0(ws.mag), fmt: f0, goodUp: true }]),
    { label: blade ? 'Swing rate' : 'Fire rate', v: 60 / s.cooldown, text: `${f0(60 / s.cooldown)} ${blade ? 'per min' : 'rpm'}`, fmt: f0, goodUp: true },
    ...(blade ? [] : [{ label: 'Reload', v: ws.reload, text: `${ws.reload.toFixed(2)} s`, fmt: (x: number) => x.toFixed(2), goodUp: false }]),
    { label: 'Range', v: s.range, text: f0(s.range), fmt: f0, goodUp: true },
  ];
}

export function inventory(m: Ctx): Screen {
  const { c, d } = m, v = m.view;
  const byUid = new Map(c.inventory.map(i => [i.uid, i]));
  const slotItem = (i: number) => (c.equipped[i] ? byUid.get(c.equipped[i]!) ?? null : null);
  const sel = byUid.get(v.item) ?? slotItem(0) ?? slotItem(1) ?? slotItem(2) ?? c.inventory[0] ?? null;
  const dmg = (it: Item) => weaponStats(it, d).damage.toFixed(1).replace(/\.0$/, '');
  // the slot "Equip" fills: the first empty one, otherwise slot 1
  const target = [0, 1, 2].find(i => !slotItem(i)) ?? 0;

  const box = (i: number) => {
    const it = slotItem(i), key = `<span class="iv-key">${i + 1}</span>`;
    if (!it) return `<div class="iv-slot empty">${key}<span class="dim">empty</span></div>`;
    return `<button class="iv-slot ${sel?.uid === it.uid ? 'on' : ''}" style="--rc:${rarityColor(it.r)}" data-view="item:${it.uid}" data-item="${it.uid}" data-k="lo${i}">${key}${weaponImg(it)}<b>${esc(weaponName(it.w))}</b></button>`;
  };
  const katana = `<div class="iv-slot"><span class="iv-key">4</span>${weaponImg(null, 'mn-wimg fist')}<b>Katana</b></div>`;
  const mi = m.snap ? maskIndex(m.snap.p, m.you) : 0, mask = CREW[mi];
  const stage = `<div class="iv-stage"><div class="iv-col">${box(0)}${box(1)}</div>
    <div class="iv-fig"><canvas id="figure" data-crew="${mi}" width="48" height="80" title="${esc(mask.name)} mask" style="filter:drop-shadow(0 0 4px ${mask.color})"></canvas></div>
    <div class="iv-col">${box(2)}${katana}</div></div>`;

  const used = new Set<string>(AMMO_TYPES.filter(a => (c.ammo[a] ?? 0) > 0));
  for (const it of c.inventory) { const a = weaponStats(it, d).spec.ammo; if (a) used.add(a); }
  const ammo = AMMO_TYPES.filter(a => used.has(a)).map(a => `${RARITIES.ammoPacks[a].name} <b>${c.ammo[a] ?? 0}</b>`).join(' · ') || 'none';
  const spare = Object.values(c.parts ?? {}).reduce((a, b) => a + b, 0);
  const kit = `<div class="iv-kit"><div class="mn-chips">${chip('Ninja stars', String(c.stars ?? 0), !c.stars ? 'warn' : '')}${chip(`${pelletSvg(14)} Smoke`, String(c.smoke ?? 0), !c.smoke ? 'warn' : '')}${chip(`${kunaiImg(14)} EMP`, String(c.emp ?? 0), !c.emp ? 'warn' : '')}<span class="dim">G throws · T switches</span></div>
    <div><span class="dim">Ammo</span> ${ammo}</div>
    <div class="iv-parts">Spare parts: ${spare ? spare : 'none'} — fit them at Hachi's gunsmith</div></div>`;

  const n = c.inventory.length, pg = pager(m, 'inv', n, PER_PAGE);
  const list = c.inventory.slice(pg.from, pg.to).map(it => {
    const slot = c.equipped.indexOf(it.uid), on = sel?.uid === it.uid;
    return `<div class="iv-row ${on ? 'on' : ''}" style="--rc:${rarityColor(it.r)}"><button class="iv-pick" data-view="item:${it.uid}" data-item="${it.uid}" data-k="it${it.uid}">${weaponImg(it)}
      <span class="nm"><b>${esc(weaponName(it.w))}</b> <em>${RARITIES.tiers[it.r].name}</em></span><span class="dmg"><i>Damage</i>${dmg(it)}</span></button>
      ${slot >= 0 ? `<span class="iv-state">Slot ${slot + 1}</span>` : btn('Equip', { t: 'equip', slot: target, uid: it.uid }, `rq${it.uid}`, false, 'iv-eq')}</div>`;
  }).join('');

  let detail = '';
  if (sel) {
    const slot = c.equipped.indexOf(sel.uid), base = slot >= 0 ? null : slotItem(target);
    const br = base ? rows(base, d) : [];
    const tr = rows(sel, d).map(r => {
      const b = br.find(x => x.label === r.label);
      return `<div><span class="dim">${r.label}</span><b>${r.text}</b>${b ? delta(r.v - b.v, r.fmt, r.goodUp) : '<span class="mn-d"></span>'}</div>`;
    }).join('');
    const note = slot >= 0 ? `In slot ${slot + 1}` : base ? `Equip replaces slot ${target + 1}: ${esc(weaponName(base.w))} (green is better)` : `Equip puts it in slot ${target + 1}, which is empty`;
    detail = `<div class="mn-detail iv-detail" style="--rc:${rarityColor(sel.r)}"><div class="dh">${weaponImg(sel, 'mn-wimg')}<div><h3>${esc(weaponName(sel.w))}</h3><em>${RARITIES.tiers[sel.r].name}</em>${weaponStats(sel, d).spec.ok === false ? ' ' + chip('', "can't fire: missing parts", 'bad') : ''}</div>
      <div class="mn-equip"><span class="dim">Equip to</span>${[0, 1, 2].map(i => btn(String(i + 1), { t: 'equip', slot: i, uid: sel.uid }, `eq${sel.uid}${i}`, slot === i, slot === i ? 'on' : '')).join('')}
      ${slot >= 0 ? btn('Unequip', { t: 'equip', slot, uid: null }, 'dtun') : ''}</div></div>
      <div class="iv-note">${note}</div><div class="iv-stats">${tr}</div></div>`;
  }

  const body = `<div class="iv"><section><h3 class="iv-h">Loadout</h3>${stage}${kit}</section>
    <section><div class="iv-bag ${n >= INV_CAP ? 'full' : ''}"><h3 class="iv-h">Your guns</h3>${pg.nav}<span class="bg">Bag ${n}/${INV_CAP}</span><span class="iv-bagbar"><i style="width:${Math.min(100, n / INV_CAP * 100)}%"></i></span></div>
    <div class="iv-list">${list || '<div class="mn-empty">You own no guns. Buy one at Hachi\'s.</div>'}</div>${detail}</section></div>`;
  return { title: 'Inventory', body, foot: 'Keys 1-3 pick slots · F cycles · 4 katana · G throw · T switch throwable · Q quickhack targeting turns 1-3 into hacks' };
}
